<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto" tabindex="0">
        <form @submit.prevent="save">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full xl:w-1/2 mx-auto md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ $t('PWA settings') }}</h1>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <router-link class="btn-stage-secondary mr-3" to="/workspace/admin/settings">
                            {{ $t('Close') }}
                        </router-link>
                        <button class="btn-stage-primary" type="submit">
                            {{ $t('Save') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <div class="w-full xl:w-1/2 mx-auto bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Activation and identity') }}</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">{{ $t('Controls installability and defines how the app is named and displayed on user devices in install prompts and launcher contexts.') }}</div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2 space-y-5">
                            <div>
                                <div class="flex items-center">
                                    <input-switch
                                        id="pwa_enabled"
                                        v-model="pwa_enabled"
                                        disabled-label="PWA aktivieren"
                                        enabled-label="PWA aktivieren"
                                    ></input-switch>
                                </div>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2" for="pwa_name">{{ $t('Application name') }}</label>
                                <div class="relative">
                                    <input
                                        id="pwa_name"
                                        v-model="pwa_name"
                                        :placeholder="$t('Application name')"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        maxlength="255"
                                        required
                                    >
                                </div>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2" for="pwa_short_name">{{ $t('Short name') }}</label>
                                <div class="relative">
                                    <input
                                        id="pwa_short_name"
                                        v-model="pwa_short_name"
                                        :placeholder="$t('Short name')"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        maxlength="32"
                                        required
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="w-full xl:w-1/2 mx-auto mt-6 bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Design and colors') }}</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">{{ $t('Defines theme and background colors for startup and app-shell rendering so launch states remain visually consistent.') }}</div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2 space-y-4">
                            <input-color
                                id="pwa_theme_color"
                                v-model="pwa_theme_color"
                                :label="$t('Theme color')"
                                :placeholder="defaultPwaThemeColor"
                                :required="true"
                            ></input-color>
                            <input-color
                                id="pwa_background_color"
                                v-model="pwa_background_color"
                                :label="$t('Background color')"
                                :placeholder="defaultPwaBackgroundColor"
                                :required="true"
                            ></input-color>
                        </div>
                    </div>
                </div>

                <div class="w-full xl:w-1/2 mx-auto mt-6 bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Icons') }}</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">{{ $t('Upload dedicated square icons for common install sizes; open the preview in a new tab to verify sharpness and final appearance.') }}</div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2 space-y-6">
                            <div>
                                <label class="stage-filter-label block mb-2">{{ $t('Icon 192x192') }}</label>
                                <div class="mt-2 flex items-center space-x-3">
                                    <input ref="icon192Input" accept="image/*" class="hidden" type="file" @change="onIconPicked($event, 'pwa_icon_192')">
                                    <a :href="iconPreview(pwa_icon_192)" class="inline-flex items-center justify-center p-1 rounded-sm border bg-white" style="border-color: var(--ui-border-default);" rel="noopener" target="_blank">
                                        <img :src="iconPreview(pwa_icon_192)" alt="PWA 192 icon" class="h-14 w-14 object-cover rounded-sm">
                                    </a>
                                    <div class="inline-flex items-center justify-center p-1 rounded-sm border bg-white" style="border-color: var(--ui-border-default);">
                                        <button class="btn-stage-secondary" type="button" @click="openIconPicker('icon192Input')">
                                            {{ $t('Upload') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2">{{ $t('Icon 512x512') }}</label>
                                <div class="mt-2 flex items-center space-x-3">
                                    <input ref="icon512Input" accept="image/*" class="hidden" type="file" @change="onIconPicked($event, 'pwa_icon_512')">
                                    <a :href="iconPreview(pwa_icon_512)" class="inline-flex items-center justify-center p-1 rounded-sm border bg-white" style="border-color: var(--ui-border-default);" rel="noopener" target="_blank">
                                        <img :src="iconPreview(pwa_icon_512)" alt="PWA 512 icon" class="h-14 w-14 object-cover rounded-sm">
                                    </a>
                                    <div class="inline-flex items-center justify-center p-1 rounded-sm border bg-white" style="border-color: var(--ui-border-default);">
                                        <button class="btn-stage-secondary" type="button" @click="openIconPicker('icon512Input')">
                                            {{ $t('Upload') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="w-full xl:w-1/2 mx-auto mt-6 bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Cache and update behavior') }}</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">{{ $t('Defines cache invalidation and rollout behavior for new app-shell releases while keeping dynamic business data server-driven.') }}</div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2 space-y-4">
                            <div>
                                <label class="stage-filter-label block mb-2" for="pwa_cache_version">{{ $t('Cache version') }}</label>
                                <div class="relative">
                                    <input
                                        id="pwa_cache_version"
                                        v-model="pwa_cache_version"
                                        :placeholder="$t('e.g. v1')"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        maxlength="50"
                                        required
                                    >
                                </div>
                            </div>
                            <div class="border rounded-sm p-3" style="border-color: var(--ui-border-default);">
                                <label class="inline-flex items-center">
                                    <input v-model="pwa_cache_enabled" class="form-checkbox h-4 w-4 text-gray-700" type="checkbox">
                                    <span class="ml-2 text-sm text-gray-800">{{ $t('Enable shell cache') }}</span>
                                </label>
                                <p class="mt-2 text-xs text-gray-500 leading-5">
                                    {{ $t('Caches static shell files such as CSS, JavaScript and icons to reduce load times on repeat visits.') }}
                                </p>
                            </div>
                            <div class="border rounded-sm p-3" style="border-color: var(--ui-border-default);">
                                <label class="inline-flex items-center">
                                    <input v-model="pwa_force_update" class="form-checkbox h-4 w-4 text-gray-700" type="checkbox">
                                    <span class="ml-2 text-sm text-gray-800">{{ $t('Force client update after release') }}</span>
                                </label>
                                <p class="mt-2 text-xs text-gray-500 leading-5">
                                    {{ $t('Activates a newly deployed app shell faster so clients do not continue using outdated cached code.') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="w-full xl:w-1/2 mx-auto mt-6 bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Counter-Badge') }}</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">{{ $t('Steuert die Anzeige des PWA Counter-Badges auf dem PWA-App-Icon und legt fest, welche Datenquelle die Ausgabe bestimmt.') }}</div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2 space-y-5">
                            <div>
                                <div class="flex items-center">
                                    <input-switch
                                        id="pwa_badge_enabled"
                                        v-model="pwa_badge_enabled"
                                        disabled-label="Counter-Badge aktivieren"
                                        enabled-label="Counter-Badge aktivieren"
                                    ></input-switch>
                                </div>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2" for="pwa_badge_counter">{{ $t('Zählerquelle') }}</label>
                                <div class="relative">
                                    <select
                                        id="pwa_badge_counter"
                                        v-model="pwa_badge_counter"
                                        :disabled="!pwa_badge_enabled"
                                        class="stage-filter-input form-select block w-full pr-10 text-sm transition duration-150 ease-in-out sm:leading-5"
                                    >
                                        <option value="unread_tickets">{{ $t('Ungelesene Tickets') }}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
import {refreshPwaBadgeCount} from "@/utils/pwa-badge";
import {UI_COLORS} from '@/utils/ui-colors';

const DEFAULT_PWA_THEME_COLOR = UI_COLORS.text.heading;
const DEFAULT_PWA_BACKGROUND_COLOR = UI_COLORS.surface.muted;

export default {
    name: "settings-pwa",
    metaInfo() {
        return {
            title: this.$i18n.t('PWA settings')
        }
    },
    data() {
        return {
            loading: true,
            defaultPwaThemeColor: DEFAULT_PWA_THEME_COLOR,
            defaultPwaBackgroundColor: DEFAULT_PWA_BACKGROUND_COLOR,
            pwa_enabled: false,
            pwa_name: null,
            pwa_short_name: null,
            pwa_theme_color: DEFAULT_PWA_THEME_COLOR,
            pwa_background_color: DEFAULT_PWA_BACKGROUND_COLOR,
            pwa_icon_192: {
                file: null,
                text: null,
                preview: null,
            },
            pwa_icon_512: {
                file: null,
                text: null,
                preview: null,
            },
            pwa_cache_enabled: true,
            pwa_cache_version: 'v1',
            pwa_force_update: true,
            pwa_badge_enabled: false,
            pwa_badge_counter: 'unread_tickets',
        }
    },
    mounted() {
        this.get();
    },
    methods: {
        iconPreview(icon) {
            if (icon && icon.preview) {
                return icon.preview;
            }
            return '/images/default/icon.png';
        },
        openIconPicker(refName) {
            if (this.$refs[refName]) {
                this.$refs[refName].click();
            }
        },
        onIconPicked(event, key) {
            const self = this;
            const files = event && event.target ? event.target.files : null;
            if (!files || !files.length) {
                return;
            }
            const file = files[0];
            if (!file.type || !file.type.startsWith('image/')) {
                self.$notify({
                    title: self.$i18n.t('Unsupported file type').toString(),
                    text: self.$i18n.t('Only image type files are accepted').toString(),
                    type: 'error'
                });
                return;
            }
            self[key].file = file;
            self[key].preview = URL.createObjectURL(file);
        },
        syncRuntimePwaSettings() {
            if (!this.$store) {
                return;
            }

            this.$store.commit('patchSettings', {
                pwa_enabled: this.pwa_enabled,
                pwa_force_update: this.pwa_force_update,
                pwa_badge_enabled: this.pwa_badge_enabled,
                pwa_badge_counter: this.pwa_badge_counter,
            });
        },
        get() {
            const self = this;
            self.loading = true;
            return axios.get('api/workspace/admin/settings/pwa').then(function (response) {
                self.pwa_enabled = response.data.pwa_enabled === null ? false : response.data.pwa_enabled;
                self.pwa_name = response.data.pwa_name || (self.$store.state.settings ? self.$store.state.settings.name : 'CORPRED desk');
                self.pwa_short_name = response.data.pwa_short_name || 'CORPRED';
                self.pwa_theme_color = response.data.pwa_theme_color || DEFAULT_PWA_THEME_COLOR;
                self.pwa_background_color = response.data.pwa_background_color || DEFAULT_PWA_BACKGROUND_COLOR;
                self.pwa_icon_192 = response.data.pwa_icon_192 || {file: null, text: '/images/default/icon.png', preview: '/images/default/icon.png'};
                self.pwa_icon_512 = response.data.pwa_icon_512 || {file: null, text: '/images/default/icon.png', preview: '/images/default/icon.png'};
                self.pwa_cache_enabled = response.data.pwa_cache_enabled === null ? true : response.data.pwa_cache_enabled;
                self.pwa_cache_version = response.data.pwa_cache_version || 'v1';
                self.pwa_force_update = response.data.pwa_force_update === null ? true : response.data.pwa_force_update;
                self.pwa_badge_enabled = response.data.pwa_badge_enabled === null ? false : response.data.pwa_badge_enabled;
                self.pwa_badge_counter = response.data.pwa_badge_counter || 'unread_tickets';
                self.syncRuntimePwaSettings();
                refreshPwaBadgeCount().catch(function () {
                });
                self.loading = false;
            }).catch(function () {
                self.loading = false;
                self.$router.push('/workspace/admin/settings');
            });
        },
        save() {
            const self = this;
            const formData = new FormData();
            self.loading = true;
            formData.append('pwa_enabled', self.pwa_enabled ? 1 : 0);
            formData.append('pwa_name', self.pwa_name);
            formData.append('pwa_short_name', self.pwa_short_name);
            formData.append('pwa_theme_color', self.pwa_theme_color);
            formData.append('pwa_background_color', self.pwa_background_color);
            formData.append('pwa_icon_192', self.pwa_icon_192 && self.pwa_icon_192.text ? self.pwa_icon_192.text : '');
            formData.append('pwa_icon_512', self.pwa_icon_512 && self.pwa_icon_512.text ? self.pwa_icon_512.text : '');
            formData.append('pwa_cache_enabled', self.pwa_cache_enabled ? 1 : 0);
            formData.append('pwa_cache_version', self.pwa_cache_version);
            formData.append('pwa_force_update', self.pwa_force_update ? 1 : 0);
            formData.append('pwa_badge_enabled', self.pwa_badge_enabled ? 1 : 0);
            formData.append('pwa_badge_counter', self.pwa_badge_counter);
            if (self.pwa_icon_192 && self.pwa_icon_192.file) {
                formData.append('icon_192', self.pwa_icon_192.file);
            }
            if (self.pwa_icon_512 && self.pwa_icon_512.file) {
                formData.append('icon_512', self.pwa_icon_512.file);
            }

            axios.post('api/workspace/admin/settings/pwa', formData, {headers: {'Content-Type': 'multipart/form-data'}}).then(function () {
                return self.get();
            }).then(function () {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Settings updated successfully').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loading = false;
            });
        }
    }
}
</script>
